import { CreditCard, Layers, TrendingUp, Wallet, type LucideIcon } from 'lucide-react'
import { accountRemovalCopy } from './useAccountDeleteFlow'
import { Link } from 'react-router'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { InstitutionAvatar } from '../../platform/primitives/InstitutionAvatar'
import { Meter } from '../../platform/primitives/Meter'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { accountHealth } from './accountHealth'
import { GROUP_ORDER, groupOf, type Group } from './accountGroups'
import { signedCents, utilisation } from './accountMoney'
import { statusInfoOf, StatusPill } from './connectionStatus'
import type { Account } from './useAccounts'

/**
 * The account card grid — M5 Ticket 3 (#425 §A5), replacing `ListRow`s in a
 * plain list. **`role="listitem"` + `aria-label={account.name}` are the ONE
 * thing this file must not change.** The plan's own Global Constraints
 * section names FOUR specs reaching a row this way; the real figure — 38
 * call sites across `e2e/specs/**` and `Accounts.test.tsx` — is not in any
 * committed document. It came from the brief that assigned this ticket
 * (an explorer's count across `e2e/`, not the plan), so it is stated here as
 * a measured fact rather than attributed to a source that does not carry it
 * (gate finding, #425 T3, blocking 5 — the eighth false docblock on this
 * epic, and the citation was invented even though the number was correct).
 */

/** One glyph per `Group`. Deliberately NOT moved into `accountGroups.ts`
 *  despite that file owning the grouping vocabulary (gate finding, #425 T3,
 *  non-blocking) — the plan's own Global Constraints says `accountGroups.ts`
 *  "is reused UNCHANGED" for this whole ticket set, restated from the
 *  original task brief, and an icon is presentation, not grouping logic; the
 *  same split `Icon.tsx`'s own docblock draws between "which surface a
 *  component IS" and "what that looks like". Living in one of `Group`'s two
 *  consumers rather than the type's own file is a real, small cost, taken
 *  on purpose rather than by omission. */
export const GROUP_ICON: Record<Group, LucideIcon> = {
  cash: Wallet,
  credit: CreditCard,
  investments: TrendingUp,
  other: Layers,
}

/** `GROUP_ORDER`'s own titles ("Cash", "Credit", …) — not a second string
 *  table. The group heading already prints this text for the SECTION; the
 *  card's own type chip repeats it once more per account so a card still
 *  reads correctly if it is ever seen outside its group's own heading. */
const GROUP_TITLE: Record<Group, string> = Object.fromEntries(
  GROUP_ORDER.map(({ group, title }) => [group, title]),
) as Record<Group, string>

/** `{{ a.balanceLabel }}` (#425 §A5) — per group, not per raw `type`. No
 *  design source names a label for `other`; "Balance" is the plain, honest
 *  catch-all rather than inventing a specific noun for a bucket that exists
 *  precisely because this client's vocabulary does not recognise the type. */
const BALANCE_LABEL: Record<Group, string> = {
  cash: 'Current',
  credit: 'Balance owed',
  investments: 'Market value',
  other: 'Balance',
}

/**
 * The credit-utilisation meter — moved from `Accounts.tsx` unchanged. Text
 * carries the percentage (rule 11), the bar repeats it.
 *
 * **Still credit-only in practice, and that is a considered outcome, not a
 * shortcut.** #425 §A5 says "extend the meter beyond credit: `utilisation()`
 * already returns `null` without a limit, so the conditional already
 * generalises" — read literally as an instruction to widen WHERE this is
 * called from, not to change what `utilisation()` computes. It has not
 * changed: `accountMoney.ts` still derives a limit only for `groupOf(a.type)
 * === 'credit'`, because a cash or investment account carries no
 * `available_balance`-as-remaining-credit concept for it to derive one from
 * — there is no backend field a limit could come from for either group. What
 * DID change is that this component no longer repeats that gate itself
 * (`Accounts.tsx`'s old `{group === 'credit' && <AccountMeter />}`): every
 * card calls `utilisation()` unconditionally and renders on whatever it
 * returns, so the ONE place credit-only-ness is decided is
 * `accountMoney.ts`, not duplicated here. If a future account type ever
 * grows a real limit, this component needs no change at all to show it.
 */
function AccountMeter({ account }: { account: Account }) {
  const u = utilisation(account)
  if (u === null) return null
  return (
    <div className="account-meter">
      <Meter
        className="account-meter__track"
        label={`${account.name} credit used`}
        value={u.pct}
        valueText={`${u.pct}% used`}
      />
      <span className="account-meter__label" aria-hidden="true">{u.pct}% used</span>
    </div>
  )
}

/**
 * The whole card activates, per #425 §A5 — but the accessible/clickable
 * control is NOT the whole card's markup wrapped in one control. That would
 * make its computed accessible name the concatenation of every visible
 * string on the card (institution, chips, balance, meter), which is both a
 * worse screen-reader experience (one long run-on name instead of
 * separately-readable facts) and a regression against `Accounts.test.tsx`'s
 * own `getByRole('link', { name: 'Chequing' })` — exact match, no
 * institution/mask/owner text folded in.
 *
 * **A real react-router `<Link>` (#433).** An anchor carries an `href`, so
 * ⌘/ctrl-click, middle-click, "copy link address" and the status-bar URL all
 * work — none of which a `<button>` calling `navigate()` can offer. Its
 * accessible name is the account name alone (role `link`, not `button`).
 *
 * Stretched the same way `merchants.css`/`categories.css`/`household.css`
 * already stretch a real control invisibly over a bigger hit target (their
 * case: a radio over its own label) — here over the WHOLE card via `::after`
 * on `.account-card__name-link`, positioned against `.account-card`'s own
 * `position: relative`. The link's own visible/accessible content stays
 * just the account name; everything else on the card remains ordinary,
 * separately-readable text, painted UNDER the invisible overlay in normal
 * document flow.
 *
 * **Accepted cost, not an oversight: text elsewhere on the card cannot be
 * selected.** The institution name, the `••mask` and the balance figure are
 * exactly what a user might want to copy, and this pattern makes none of
 * them selectable — the overlay intercepts the mousedown that would start a
 * selection anywhere but the link's own ~20px text. This is `.stretched-
 * link`'s own documented tradeoff, not specific to this port, and it is
 * accepted here rather than fixed so it does not get silently re-litigated.
 *
 * `useHitTarget('page')`, not `'panel'` — corrected at re-gate. `ListRow
 * .tsx`'s own floor was dropped when this replaced it (#425 T3 gate,
 * blocking 2) and restored here as an INLINE `min-height`, the one thing
 * that can beat a future `useBreakpoint()` stacking rule the way M0-R29
 * already documents for `Button`. `'panel'` (30px) was the WRONG floor,
 * though: `e2e/hitTargets.mjs`'s own `fineFloorFor` classifies a control by
 * its CLASSES — `btn--size-panel` for the 30px floor, `btn--size-inline`/
 * `inline-link` for 24px, `page` (33px) for anything else, "the strictest
 * of the three and therefore the safe default." This link is hand-rolled,
 * carries none of `Button`'s size classes, and MUST NOT be given
 * `btn--size-panel` just to justify 30px — `primitives.css` gives that
 * class real padding, which would import a visual change to satisfy a
 * classifier rather than fix the floor. `'page'` is what the sweep already
 * measures this element against, so it is what this reads too. The visible
 * text sits in its own inner span so the ellipsis and the taller, centred
 * hit box do not fight over the same box (`ListRow.tsx`'s own split between
 * its control and `.list-row__label`).
 *
 * `.account-card__actions` (Edit/Delete) needs its own `position: relative`
 * to be reachable at all — an absolutely-positioned element with `z-index:
 * auto` paints after every non-positioned, in-flow box regardless of DOM
 * order, so without this the overlay would sit on top of Edit/Delete and
 * swallow every click meant for them. Once `.account-card__actions` is
 * ALSO positioned, both boxes compete in the same paint step and DOM ORDER
 * settles it — the later element wins — which is why it must stay declared
 * AFTER the identity block in this component's JSX, the same source-order
 * dependency `accounts-summary--phone`'s own docblock documents for the
 * identical reason. No `z-index` needed on either side; this is the exact
 * mechanism Bootstrap's `.stretched-link` documents, not a bespoke one.
 */
function CardActivateLink({ account }: { account: Account }) {
  const minHeight = useHitTarget('page')
  return (
    <Link to={`/accounts/${account.id}`} className="account-card__name-link" style={{ minHeight }}>
      <span className="account-card__name-link-text">{account.name}</span>
    </Link>
  )
}

/**
 * Edit is manual-only — `AccountUpdate` has no field a linked account's own
 * card could usefully change from here, the same reasoning `Accounts.tsx`'s
 * old `AccountRow` already applied. Delete is universal (`useAccountDeleteFlow`
 * covers both provider kinds, with a different confirmation body per one).
 *
 * **Rehoused here, not behind an overflow toggle.** #425 §A5 offers either an
 * overflow affordance or deferring to `/accounts/:id`; this picks neither,
 * because `e2e/specs/accounts.spec.ts:263-291,404` and this ticket's own
 * `AccountDeleteFlow` coverage both click `row.getByRole('button', { name:
 * 'Edit' | 'Delete', exact: true })` directly, with no prior "open the menu"
 * step — those specs are locked (a tester owns `e2e/**`), so a control that
 * requires a click to reveal before it can be clicked would break them
 * outright. Always-visible chip buttons preserve every one of those
 * assertions; the pixel-fidelity cost is that the design's card has no
 * visual slot for them, so this is a stated, deliberate departure rather
 * than the overflow menu the design implies.
 */
function CardActions({
  account,
  onEdit,
  onDelete,
}: {
  account: Account
  onEdit: (account: Account) => void
  onDelete: (account: Account) => void
}) {
  const manual = account.provider === 'manual'
  return (
    <div className="account-card__actions">
      {manual && (
        <Button variant="chip" onClick={() => onEdit(account)}>
          Edit
        </Button>
      )}
      <Button variant="chip" onClick={() => onDelete(account)}>
        {accountRemovalCopy(account).action}
      </Button>
    </div>
  )
}

export function AccountCard({
  account,
  onEdit,
  onDelete,
}: {
  account: Account
  onEdit: (account: Account) => void
  onDelete: (account: Account) => void
}) {
  const group = groupOf(account.type)
  const cents = account.currentCents === null ? null : signedCents(account.currentCents, group)
  const health = accountHealth(account)

  return (
    <Panel role="listitem" aria-label={account.name} className="account-card">
      <div className="account-card__identity">
        {/* A monogram, never a logo — #425 §A5 says "institution monogram
            (from institutionName)", and `Account` carries no logo field at
            all (only `ProviderItem` does, `InstitutionsPanel.tsx`'s own
            join target). `institutionName` falls back to the account's own
            name so a manual account with no institution still gets sensible
            initials rather than an empty monogram. */}
        <InstitutionAvatar name={account.institutionName ?? account.name} logoUrl={null} />
        <span className="account-card__identity-text">
          <CardActivateLink account={account} />
          {account.institutionName && (
            <span className="account-card__institution">{account.institutionName}</span>
          )}
        </span>
        {/* `.account-card__status`'s own `position: relative` (accounts.css)
            is what makes this reachable at all (#425 T3 gate, blocking 3):
            the overlay covers everything in normal flow, including the
            pill, so its `title` never fires on hover and there is no
            keyboard or touch path to it either. Positioning this wrapper
            wins the same paint-order tie `.account-card__actions` already
            relies on (`CardActivateLink`'s own docblock), which restores
            the NATIVE hover tooltip for a sighted mouse user. The `sr-only`
            sibling is the other half — real, perceivable text a screen
            reader encounters reading through the card regardless of hover
            or focus, carrying the exact same long form `StatusPill` already
            computes (`statusInfoOf`, reused rather than re-derived) so nothing
            here can drift from what the pill itself says. */}
        <span className="account-card__status">
          <StatusPill health={health} />
          <span className="sr-only">{` — ${statusInfoOf(health).title}`}</span>
        </span>
      </div>

      <div className="account-card__chips">
        <span className="account-card__chip">
          <Icon glyph={GROUP_ICON[group]} size={13} />
          {GROUP_TITLE[group]}
        </span>
        {account.mask && (
          <span className="account-card__chip account-card__chip--mono">{`••${account.mask}`}</span>
        )}
        {account.ownerName && <span className="account-card__chip">{account.ownerName}</span>}
        <span className="account-card__chip">{account.currency}</span>
      </div>

      <div className="account-card__balance">
        <span className="account-card__balance-label">{BALANCE_LABEL[group]}</span>
        {/* No fallback dash when `cents === null` — `Accounts.tsx`'s old
            `AccountRow` rendered nothing in this case too; a missing balance
            is absent, not a fabricated zero. */}
        {cents !== null && <Money cents={cents} currency={account.currency} kind="stock" />}
      </div>

      <AccountMeter account={account} />

      <CardActions account={account} onEdit={onEdit} onDelete={onDelete} />
    </Panel>
  )
}

/**
 * §A6 — a dashed tile at the end of a POPULATED card grid, not only in the
 * empty state's `READ_ONLY_NOTE`. No nested interactive content, so this is
 * simply a real `<button>` covering the whole tile — none of `AccountCard`'s
 * stretched-link machinery is needed when there is nothing else inside it to
 * keep clickable.
 */
export function ConnectAnotherTile({ onActivate }: { onActivate: () => void }) {
  return (
    <button type="button" className="account-connect-tile" onClick={onActivate}>
      <span className="account-connect-tile__icon" aria-hidden="true">
        ＋
      </span>
      <span className="account-connect-tile__title">Connect another institution</span>
      <span className="account-connect-tile__body">
        Read-only bank links through Plaid or Flinks, or add a manual balance you update yourself.
      </span>
    </button>
  )
}
